import React from 'react';
import { CalendarClock, Check } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import { Input } from '@cotera/client/v0/components/ui/input';

/** Date-range presets; `ms` is the lookback window (null = all time). */
const DATE_RANGE_PRESETS: { key: string; label: string; ms: number | null }[] =
  [
    { key: 'all', label: 'All time', ms: null },
    { key: '1h', label: 'Last hour', ms: 60 * 60 * 1000 },
    { key: '24h', label: 'Last 24 hours', ms: 24 * 60 * 60 * 1000 },
    { key: '7d', label: 'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000 },
    { key: '30d', label: 'Last 30 days', ms: 30 * 24 * 60 * 60 * 1000 },
  ];

export type DateRangeValue =
  | { kind: 'preset'; key: string }
  /** Calendar dates as `yyyy-mm-dd` in local time; either side may be open. */
  | { kind: 'custom'; from: string | null; to: string | null };

/** Runs default to the last week; "All time" remains selectable. */
export const DEFAULT_DATE_RANGE: DateRangeValue = { kind: 'preset', key: '7d' };

/** Whether a selection is the default range, i.e. not narrowing the runs. */
export const isDefaultDateRange = (value: DateRangeValue): boolean =>
  value.kind === 'preset' && value.key === DEFAULT_DATE_RANGE.key;

/** Preset cutoffs snap to this boundary so cache keys stay stable across remounts. */
const PRESET_CUTOFF_STEP_MS = 5 * 60 * 1000;

/**
 * Resolves a date-range selection to inclusive ISO `created_at` bounds.
 * Presets are lookback windows from "now" (rounded down to a 5-minute
 * boundary); custom dates span the full local day on each side.
 */
export function dateRangeBounds(value: DateRangeValue): {
  createdAfter?: string;
  createdBefore?: string;
} {
  if (value.kind === 'preset') {
    const ms = DATE_RANGE_PRESETS.find((r) => r.key === value.key)?.ms ?? null;
    if (ms === null) {
      return {};
    }
    const cutoff =
      Math.floor((Date.now() - ms) / PRESET_CUTOFF_STEP_MS) *
      PRESET_CUTOFF_STEP_MS;
    return { createdAfter: new Date(cutoff).toISOString() };
  }
  const bounds: { createdAfter?: string; createdBefore?: string } = {};
  if (value.from !== null && value.from !== '') {
    bounds.createdAfter = new Date(`${value.from}T00:00:00`).toISOString();
  }
  if (value.to !== null && value.to !== '') {
    bounds.createdBefore = new Date(`${value.to}T23:59:59.999`).toISOString();
  }
  return bounds;
}

const formatCustomDate = (date: string): string =>
  new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

function dateRangeLabel(value: DateRangeValue): string {
  if (value.kind === 'preset') {
    return (
      DATE_RANGE_PRESETS.find((r) => r.key === value.key)?.label ?? 'All time'
    );
  }
  const from =
    value.from !== null && value.from !== ''
      ? formatCustomDate(value.from)
      : null;
  const to =
    value.to !== null && value.to !== '' ? formatCustomDate(value.to) : null;
  if (from !== null && to !== null) {
    return `${from} – ${to}`;
  }
  if (from !== null) {
    return `From ${from}`;
  }
  if (to !== null) {
    return `Until ${to}`;
  }
  return 'Custom range';
}

export const DateRangeFilter: React.FC<{
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
}> = ({ value, onChange }) => {
  const isFiltered = !isDefaultDateRange(value);
  const customFrom = value.kind === 'custom' ? value.from ?? '' : '';
  const customTo = value.kind === 'custom' ? value.to ?? '' : '';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-sm',
            'text-muted-foreground hover:text-foreground hover:bg-secondary',
            'transition-colors cursor-pointer',
            isFiltered && 'text-foreground'
          )}
        >
          <CalendarClock className="h-4 w-4" />
          <span>{dateRangeLabel(value)}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {DATE_RANGE_PRESETS.map((range) => (
          <DropdownMenuItem
            key={range.key}
            className="cursor-pointer flex items-center justify-between"
            onClick={() => onChange({ kind: 'preset', key: range.key })}
          >
            <span>{range.label}</span>
            {value.kind === 'preset' && range.key === value.key ? (
              <Check className="h-4 w-4 text-muted-foreground" />
            ) : null}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Custom range</span>
          {value.kind === 'custom' ? (
            <Check className="h-4 w-4 text-muted-foreground" />
          ) : null}
        </DropdownMenuLabel>
        {/* Inputs live inside the menu; stop keydown so Radix typeahead doesn't steal keystrokes. */}
        <div
          className="px-2 pb-2 flex flex-col gap-2"
          onKeyDown={(e) => e.stopPropagation()}
        >
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="w-8">From</span>
            <Input
              type="date"
              className="h-8"
              value={customFrom}
              max={customTo !== '' ? customTo : undefined}
              onChange={(e) =>
                onChange({
                  kind: 'custom',
                  from: e.target.value === '' ? null : e.target.value,
                  to: customTo === '' ? null : customTo,
                })
              }
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="w-8">To</span>
            <Input
              type="date"
              className="h-8"
              value={customTo}
              min={customFrom !== '' ? customFrom : undefined}
              onChange={(e) =>
                onChange({
                  kind: 'custom',
                  from: customFrom === '' ? null : customFrom,
                  to: e.target.value === '' ? null : e.target.value,
                })
              }
            />
          </label>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
